import { useEffect, useRef, useState } from "react";
import { Phone, X, Copy, Check, Video, ShieldCheck, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "../lib/api";

const CHUNK_MS = 6000;          // upload a chunk every ~6s
const MAX_DURATION_MS = 60 * 60 * 1000; // safety stop after 60min

/**
 * Real video consultation via Jitsi Meet (free, encrypted) + browser-side recording.
 * Flow:
 *   1) Patient/doctor opens modal — sees a consent popup.
 *   2) On YES: we request mic+camera via getUserMedia, start MediaRecorder, and embed Jitsi iframe.
 *      Chunks are uploaded every CHUNK_MS to /api/recordings/chunk; on close we /api/recordings/finalize.
 *   3) On NO: we close the modal so the user can keep chatting.
 */
export function VideoCallModal({ open, onClose, consultationId, remoteName = "Remote", localName = "You", currentRole = "patient" }) {
    const { t } = useTranslation();
    const roomName = `MedxAI-${consultationId}`;
    const jitsiUrl = `https://meet.jit.si/${roomName}#userInfo.displayName=%22${encodeURIComponent(localName)}%22&config.prejoinPageEnabled=false&config.startWithAudioMuted=false&config.startWithVideoMuted=false`;

    // Lifecycle states: 'consent' | 'joined' | null (modal hidden)
    const [phase, setPhase] = useState("consent");
    const [copied, setCopied] = useState(false);
    const [recording, setRecording] = useState(false);

    // refs for MediaRecorder lifecycle
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunkIndexRef = useRef(0);
    const startedAtRef = useRef(null);

    // Reset phase whenever the modal is reopened
    useEffect(() => {
        if (open) setPhase("consent");
        else stopRecording(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const handle = (e) => { if (e.key === "Escape") handleEnd(); };
        window.addEventListener("keydown", handle);
        return () => window.removeEventListener("keydown", handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const blobToDataUrl = (blob) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

    const uploadChunk = async (blob) => {
        try {
            const data_url = await blobToDataUrl(blob);
            await api.post("/recordings/chunk", {
                consultation_id: consultationId,
                chunk_index: chunkIndexRef.current,
                total_chunks: -1, // unknown until finalize
                mime_type: blob.type || "video/webm",
                data_url,
            });
            chunkIndexRef.current += 1;
        } catch (e) {
            // Don't break the call if a chunk fails to upload
            console.warn("Recording chunk upload failed", e);
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: { ideal: 640 }, height: { ideal: 480 } },
                audio: true,
            });
            streamRef.current = stream;
            // Prefer WebM, but Safari may need MP4
            const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
                ? "video/webm;codecs=vp9,opus"
                : MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
                ? "video/webm;codecs=vp8,opus"
                : "video/webm";
            const rec = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 600_000 });
            rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) uploadChunk(e.data); };
            rec.start(CHUNK_MS);
            recorderRef.current = rec;
            chunkIndexRef.current = 0;
            startedAtRef.current = Date.now();
            setRecording(true);
            // safety auto-stop
            setTimeout(() => stopRecording(true), MAX_DURATION_MS);
        } catch (e) {
            console.warn("getUserMedia failed", e);
            toast.warning("Camera/microphone unavailable. The call will continue but won't be recorded. Please grant permission and try again.");
        }
    };

    const stopRecording = async (alsoFinalize = true) => {
        try {
            const rec = recorderRef.current;
            if (rec && rec.state !== "inactive") rec.stop();
        } catch {}
        try {
            const stream = streamRef.current;
            if (stream) stream.getTracks().forEach((tr) => tr.stop());
        } catch {}
        recorderRef.current = null;
        streamRef.current = null;
        if (alsoFinalize && startedAtRef.current) {
            const duration = Math.round((Date.now() - startedAtRef.current) / 1000);
            try {
                await api.post("/recordings/finalize", {
                    consultation_id: consultationId,
                    duration_seconds: duration,
                });
            } catch {}
            startedAtRef.current = null;
        }
        setRecording(false);
    };

    const consentYes = async () => {
        setPhase("joined");
        // Kick off recording in background (non-blocking)
        startRecording();
    };

    const consentNo = () => {
        onClose?.("declined");
    };

    const handleEnd = () => {
        stopRecording(true);
        onClose?.();
    };

    const copyLink = async () => {
        const shareUrl = `https://meet.jit.si/${roomName}`;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Meeting link copied to clipboard");
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Could not copy");
        }
    };

    if (!open) return null;

    // CONSENT POPUP
    if (phase === "consent") {
        return (
            <div className="fixed inset-0 z-[80] bg-black/70 grid place-items-center p-4" data-testid="video-consent-modal">
                <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-7 relative">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white shrink-0">
                            <Video className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="text-xs font-mono tracking-[0.2em] text-[var(--medx-red)]">MEDX AI · LIVE CONSULT</div>
                            <div className="font-display text-lg font-semibold">{t("consult.video.consentTitle")}</div>
                        </div>
                    </div>
                    <div className="flex gap-3 items-start bg-amber-50 border border-amber-100 rounded-xl p-4 mb-4">
                        <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-900">{t("consult.video.consentBody")}</p>
                    </div>
                    <div className="text-xs text-gray-500 mb-4 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-green-600" /> Encrypted in transit · Visible only to your treating doctor & Medx AI admin
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={consentNo}
                            className="btn-outline inline-flex items-center justify-center gap-2 !py-2.5"
                            data-testid="video-consent-no"
                        >
                            <X className="w-4 h-4" /> {t("consult.video.consentNo")}
                        </button>
                        <button
                            onClick={consentYes}
                            className="btn-primary inline-flex items-center justify-center gap-2 !py-2.5"
                            data-testid="video-consent-yes"
                        >
                            <Check className="w-4 h-4" /> {t("consult.video.consentYes")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // FULL VIDEO CALL
    return (
        <div className="fixed inset-0 z-[60] bg-black grid place-items-stretch" data-testid="video-call-modal">
            <div className="absolute top-0 left-0 right-0 z-10 px-5 py-3 flex items-center justify-between text-white bg-gradient-to-b from-black/80 to-transparent">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center shrink-0">
                        <Phone className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] font-mono tracking-[0.22em] text-orange-200 flex items-center gap-2">
                            LIVE · END-TO-END ENCRYPTED
                            {recording && (
                                <span className="inline-flex items-center gap-1.5 text-red-300" data-testid="video-recording-indicator">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                    {t("consult.video.recording")}
                                </span>
                            )}
                        </div>
                        <div className="font-semibold truncate">{remoteName}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={copyLink} className="px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-xs font-mono tracking-wider flex items-center gap-1.5" data-testid="video-copy-link">
                        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {t("consult.video.shareLink")}
                    </button>
                    <button onClick={handleEnd} className="w-10 h-10 rounded-full bg-red-600 hover:bg-red-700 grid place-items-center" data-testid="video-end" aria-label={t("consult.video.endCall")}>
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            <iframe
                title="Medx AI · Live consult"
                src={jitsiUrl}
                allow="camera; microphone; fullscreen; display-capture; autoplay"
                className="w-full h-full border-0"
                data-testid="video-jitsi-iframe"
            />

            <div className="absolute bottom-2 left-0 right-0 text-center text-[10px] text-white/40 font-mono tracking-widest z-10">
                ROOM · {roomName} · {currentRole.toUpperCase()} · BOTH PARTICIPANTS AUTOMATICALLY JOIN
            </div>
        </div>
    );
}
